import backendAxios from '../utils/backendAxios';

export const getBookedSlots = async (serviceId, fecha) => {
  try {
    const res = await backendAxios.get(`/api/bookings/slots/${serviceId}/${fecha}`);
    return res.data;
  } catch (err) {
    console.error('Error fetching booked slots', err);
    return [];
  }
};

export const createBooking = (data) => async () => {
  try {
    const res = await backendAxios.post('/api/bookings', data);
    return res.data;
  } catch (err) {
    console.error('Error creating booking', err);
    // Devuelve el error del backend (p. ej. tarjeta rechazada) para mostrarlo al usuario
    return err.response?.data || null;
  }
};

export const getProviderCalendar = async (month, year) => {
  try {
    const res = await backendAxios.get(`/api/bookings/provider/calendar?month=${month}&year=${year}`);
    return res.data;
  } catch (err) {
    console.error('Error fetching provider calendar', err);
    return [];
  }
};

export const completeBooking = async (bookingId, pin = '') => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/complete`, { pin });
    return res.data;
  } catch (err) {
    console.error('Error completing booking', err);
    // Devuelve el error del backend (p. ej. PIN incorrecto) para mostrarlo
    return err.response?.data || null;
  }
};

// Extras del servicio (cargos agregados por el proveedor durante un servicio confirmado)
export const addExtra = async (bookingId, concepto, monto) => {
  try {
    const res = await backendAxios.post(`/api/bookings/${bookingId}/extras`, { concepto, monto });
    return res.data;
  } catch (err) {
    console.error('Error agregando extra', err);
    return err.response?.data || null;
  }
};

export const removeExtra = async (bookingId, extraId) => {
  try {
    const res = await backendAxios.delete(`/api/bookings/${bookingId}/extras/${extraId}`);
    return res.data;
  } catch (err) {
    console.error('Error quitando extra', err);
    return err.response?.data || null;
  }
};

// Reporta un problema con un servicio que ya ocurrió (abre disputa; el pago se congela).
// Envía motivo (categoría), descripcion (detalle) y evidencias (archivos) como multipart.
export const reportarProblema = async (bookingId, { motivo, descripcion, archivos = [] }) => {
  try {
    const fd = new FormData();
    fd.append('motivo', motivo);
    fd.append('descripcion', descripcion);
    archivos.forEach((archivo) => fd.append('evidencias', archivo));
    // multipart/form-data para que el backend reciba los archivos en req.files;
    // se sobreescribe el Content-Type JSON por defecto de backendAxios
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/disputa`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  } catch (err) {
    console.error('Error reportando problema', err);
    return err.response?.data || null;
  }
};

// Abre una aclaración/queja sobre un servicio YA finalizado (canal de soporte; no
// congela dinero). Envía motivo, descripcion y evidencias como multipart.
export const crearAclaracion = async (bookingId, { motivo, descripcion, archivos = [] }) => {
  try {
    const fd = new FormData();
    fd.append('motivo', motivo);
    fd.append('descripcion', descripcion);
    archivos.forEach((archivo) => fd.append('evidencias', archivo));
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/aclaracion`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  } catch (err) {
    console.error('Error creando aclaración', err);
    return err.response?.data || null;
  }
};

// El cliente paga los extras pendientes (tarjeta al momento o referencia SPEI/tienda)
export const pagarExtras = async (bookingId) => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/extras/pagar`);
    return res.data;
  } catch (err) {
    console.error('Error pagando extras', err);
    return err.response?.data || null;
  }
};

// Valida un código promocional para un servicio (su categoría permite rechazar códigos
// con alcance limitado). Devuelve { valido, tipo, descuentoPct, montoFijo, montoMinimo, codigo }
// o { valido:false, msg }.
export const validarPromo = async (codigo, categoria = '') => {
  try {
    const qs = categoria ? `?categoria=${encodeURIComponent(categoria)}` : '';
    const res = await backendAxios.get(`/api/promos/${encodeURIComponent(codigo)}/validar${qs}`);
    return res.data;
  } catch (err) {
    return err.response?.data || { valido: false, msg: 'No se pudo validar el código.' };
  }
};

export const getProviderStats = async () => {
  try {
    const res = await backendAxios.get('/api/bookings/provider/stats');
    return res.data;
  } catch (err) {
    console.error('Error fetching provider stats', err);
    return { pendiente: 0, activo: 0, proximo: 0, finalizado: 0 };
  }
};

// Convierte { desde, hasta, estados } en query string (solo los que tienen valor).
// estados puede ser un array de status o un string separado por comas.
const filtrosAQuery = (filtros = {}) => {
  const params = new URLSearchParams();
  if (filtros.desde) params.append('desde', filtros.desde);
  if (filtros.hasta) params.append('hasta', filtros.hasta);
  // Array (aunque esté vacío) => siempre se envía, para que "ninguno seleccionado"
  // signifique cero resultados y no caer al default del backend.
  if (Array.isArray(filtros.estados)) {
    params.append('estados', filtros.estados.join(','));
  } else if (filtros.estados) {
    params.append('estados', filtros.estados);
  }
  const qs = params.toString();
  return qs ? `&${qs}` : '';
};

export const getProviderBookings = async (tab = 'pendiente', page = 1, filtros = {}) => {
  try {
    const res = await backendAxios.get(`/api/bookings/provider?tab=${tab}&page=${page}${filtrosAQuery(filtros)}`);
    return res.data;
  } catch (err) {
    console.error('Error fetching provider bookings', err);
    return { bookings: [], total: 0, pages: 1 };
  }
};

export const updateBookingStatus = async (bookingId, status, motivo = '') => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/status`, { status, motivo });
    return res.data;
  } catch (err) {
    console.error('Error updating booking status', err);
    // Devuelve el error del backend (p. ej. cobro rechazado al aceptar) para mostrarlo
    return err.response?.data || null;
  }
};

export const getClientStats = async () => {
  try {
    const res = await backendAxios.get('/api/bookings/client/stats');
    return res.data;
  } catch (err) {
    console.error('Error fetching client stats', err);
    return { pendiente: 0, activo: 0, proximo: 0, finalizado: 0, caducado: 0 };
  }
};

export const solicitarCambio = async (bookingId, fechaNueva, horaNueva) => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/solicitar-cambio`, { fechaNueva, horaNueva });
    return res.data;
  } catch (err) {
    console.error('Error solicitando cambio', err);
    // Devuelve el error del backend (p. ej. ya hay una solicitud pendiente) para mostrarlo
    return err.response?.data || null;
  }
};

export const responderCambio = async (bookingId, aceptar) => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/responder-cambio`, { aceptar });
    return res.data;
  } catch (err) {
    console.error('Error respondiendo cambio', err);
    return err.response?.data || null;
  }
};

export const cancelClientBooking = async (bookingId) => {
  try {
    const res = await backendAxios.patch(`/api/bookings/${bookingId}/cancel`);
    return res.data;
  } catch (err) {
    console.error('Error cancelling booking', err);
    // Devuelve el error del backend (p. ej. faltan menos de 3 h) para mostrarlo
    return err.response?.data || null;
  }
};

export const getClientBookings = async (tab = 'pendiente', page = 1, filtros = {}) => {
  try {
    const res = await backendAxios.get(`/api/bookings/client?tab=${tab}&page=${page}${filtrosAQuery(filtros)}`);
    return res.data;
  } catch (err) {
    console.error('Error fetching client bookings', err);
    return { bookings: [], total: 0, pages: 1 };
  }
};
